/**
 * Phone number helpers for North American Numbering Plan (NANP) — Canada + US.
 * 10 digits required (3-digit area code + 3-digit central office + 4-digit line),
 * with an optional leading "1" country code which we strip on normalize.
 *
 * No external dependency. If we ever need international numbers, swap in
 * libphonenumber-js (the "min" build is ~20KB gzipped and supports NANP +
 * any other country we care about).
 */

/** Strip everything except digits. Returns "" if the input is empty/null. */
export function digitsOnly(input: string | null | undefined): string {
  return (input ?? '').replace(/\D/g, '');
}

/** True if the input is a valid 10-digit NANP number (with or without leading 1). */
export function isValidNANP(input: string | null | undefined): boolean {
  const d = digitsOnly(input);
  if (d.length === 10) return /^[2-9]\d{9}$/.test(d);
  if (d.length === 11 && d.startsWith('1')) return /^1[2-9]\d{9}$/.test(d);
  return false;
}

/**
 * Format a NANP number for display: "(416) 555-1234".
 * Leaves the input unchanged if it's not a valid 10/11 digit number — that
 * way we never silently mangle data the user is mid-typing.
 */
export function formatNANP(input: string | null | undefined): string {
  const d = digitsOnly(input);
  const ten = d.length === 11 && d.startsWith('1') ? d.slice(1) : d;
  if (ten.length !== 10) return input ?? '';
  return `(${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}`;
}

/**
 * Format as the user types. Used by onChange handlers so the field shows
 * "(416) 555" while they're typing rather than waiting for blur. Returns the
 * partially-formatted string for any prefix length 0..10 of digits.
 */
export function formatAsTyping(input: string): string {
  const d = digitsOnly(input).slice(0, 10);
  if (d.length === 0) return '';
  if (d.length <= 3) return `(${d}`;
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

/**
 * Canonical storage form: 10 digits, no formatting. Useful when persisting.
 * Returns null if the input isn't a valid NANP number.
 */
export function normalizeNANPForStorage(input: string | null | undefined): string | null {
  const d = digitsOnly(input);
  if (d.length === 11 && d.startsWith('1')) return d.slice(1);
  if (d.length === 10 && /^[2-9]\d{9}$/.test(d)) return d;
  return null;
}

/** Friendly validation error message for forms. */
export const PHONE_VALIDATION_HINT =
  'Phone must be 10 digits — e.g. (416) 555-1234.';
